// API Client for Advanced Features
// Centralized API communication layer for all 7 features

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAdvancedApiBaseUrl, getMainApiBaseUrl } from '@/config/apiEnv';
import { useAuthStore, triggerAuthRedirect } from '@/stores/authStore';
import { requestLlmChatViaWebSocket } from './llm-chat-ws';

export interface TabPreview {
  id: string;
  tab_id: string;
  title: string;
  url: string;
  favicon?: string | null;
  screenshot?: string;
  thumbnail?: string;
  width?: number;
  height?: number;
  format?: string;
  size_bytes?: number;
  captured_at: string;
  is_stale: boolean;
  metadata?: Record<string, unknown>;
}

export interface TabPreviewStats {
  total_previews: number;
  total_size_bytes: number;
  stale_count: number;
  by_format: Record<string, number>;
  average_size_bytes?: number;
}

const AI_DEV_USER_KEY = 'khiimori_ai_dev_user_id';
const AI_SESSION_KEY = 'khiimori_ai_session_id';

/** Logged-in user id, or a stable anonymous id for local AI features. */
export function resolveAiUserId(authUserId?: string | null): string {
  if (authUserId) return authUserId;
  let id = localStorage.getItem(AI_DEV_USER_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(AI_DEV_USER_KEY, id);
  }
  return id;
}

export function getOrCreateAiSessionId(): string {
  let s = sessionStorage.getItem(AI_SESSION_KEY);
  if (!s) {
    s = crypto.randomUUID();
    sessionStorage.setItem(AI_SESSION_KEY, s);
  }
  return s;
}

let apiClientIsRefreshing = false;
let apiClientFailedRefreshQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processApiClientRefreshQueue(error: unknown, newToken: string | null) {
  apiClientFailedRefreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(newToken);
    }
  });
  apiClientFailedRefreshQueue = [];
}

function attachAuthInterceptors(instance: AxiosInstance): void {
  // Request interceptor for auth
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = useAuthStore.getState().tokens?.token || localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling (shared 401-refresh logic)
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Guard: never retry the refresh endpoint itself to prevent infinite loops
      const isRefreshRequest =
        originalRequest?.url?.includes('/auth/refresh') || originalRequest?.url?.endsWith('/refresh');

      if (error.response?.status === 401 && !originalRequest?._retry && !isRefreshRequest) {
        // Only attempt refresh + force-logout if the user actually
        // had a token. An anonymous 401 (e.g. a page that calls
        // /api/... before auth completed) must NOT blow away the
        // session.
        const hadToken = Boolean(
          useAuthStore.getState().tokens?.token || localStorage.getItem('authToken'),
        );
        if (!hadToken) {
          const data = error.response?.data as { message?: string; error?: string } | undefined;
          const message = data?.message || data?.error || error.message || 'Request failed';
          return Promise.reject(new Error(message));
        }
        if (apiClientIsRefreshing) {
          // Queue this request until the ongoing refresh completes
          return new Promise<unknown>((resolve, reject) => {
            apiClientFailedRefreshQueue.push({ resolve, reject });
          }).then((newToken) => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return instance(originalRequest);
          });
        }

        originalRequest._retry = true;
        apiClientIsRefreshing = true;

        try {
          await useAuthStore.getState().refreshToken();
          const refreshedToken = useAuthStore.getState().tokens?.token;
          processApiClientRefreshQueue(null, refreshedToken || null);
          apiClientIsRefreshing = false;

          if (refreshedToken) {
            originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
            return instance(originalRequest);
          }
        } catch {
          processApiClientRefreshQueue(new Error('Token refresh failed'), null);
          apiClientIsRefreshing = false;
        }

        await useAuthStore.getState().logout();
        triggerAuthRedirect();
      }

      const data = error.response?.data as { message?: string; error?: string } | undefined;
      const message = data?.message || data?.error || error.message || 'Request failed';
      return Promise.reject(new Error(message));
    }
  );
}

class ApiClient {
  public client: AxiosInstance;
  public mainClient: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: getAdvancedApiBaseUrl(),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    attachAuthInterceptors(this.client);

    // Separate axios instance for endpoints that live on the main Go
    // API (e.g. /tab-preview/...) rather than the advanced/Express
    // API. Same auth + 401-refresh behavior, different base URL.
    this.mainClient = axios.create({
      baseURL: getMainApiBaseUrl(),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    attachAuthInterceptors(this.mainClient);
  }

  // Tab Memory API
  async getTabMemories(filters: {
    tag?: string;
    pinned?: boolean;
    groupId?: string;
  } = {}): Promise<any[]> {
    const params: Record<string, string> = {};
    if (filters.tag) params.tag = filters.tag;
    if (filters.pinned) params.pinned = 'true';
    if (filters.groupId) params.group_id = filters.groupId;
    const response = await this.client.get('/tab-memory', { params });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.memories && Array.isArray(data.memories)) return data.memories;
    return [];
  }

  async getTrashedTabMemories(): Promise<any[]> {
    const response = await this.client.get('/tab-memory/trash');
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.memories && Array.isArray(data.memories)) return data.memories;
    return [];
  }

  async getTabMemory(id: string): Promise<any> {
    const response = await this.client.get(`/tab-memory/${id}`);
    return response.data;
  }

  async listTabMemoryGroups(): Promise<any[]> {
    const response = await this.client.get('/tab-memory/groups');
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.groups)) return data.groups;
    return [];
  }

  async createTabMemoryGroup(data: { id?: string; name: string; color?: string }): Promise<any> {
    const response = await this.client.post('/tab-memory/groups', data);
    return response.data;
  }

  async updateTabMemoryGroup(id: string, data: { name?: string; color?: string }): Promise<any> {
    const response = await this.client.put(`/tab-memory/groups/${id}`, data);
    return response.data;
  }

  async deleteTabMemoryGroup(id: string): Promise<void> {
    await this.client.delete(`/tab-memory/groups/${id}`);
  }

  async getTabMemoriesForGroup(groupId: string): Promise<any[]> {
    return this.getTabMemories({ groupId });
  }

  async createTabMemory(data: any): Promise<any> {
    const response = await this.client.post('/tab-memory', data);
    return response.data;
  }

  async updateTabMemory(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/tab-memory/${id}`, data);
    return response.data;
  }

  async deleteTabMemory(id: string): Promise<void> {
    await this.client.delete(`/tab-memory/${id}`);
  }

  async trashTabMemory(id: string): Promise<void> {
    await this.client.post(`/tab-memory/${id}/trash`);
  }

  async restoreTabMemory(id: string): Promise<void> {
    await this.client.post(`/tab-memory/${id}/restore`);
  }

  async recordTabMemoryAccess(id: string): Promise<void> {
    await this.client.post(`/tab-memory/${id}/access`);
  }

  async bulkDeleteTabMemories(ids: string[], hard = false): Promise<void> {
    await this.client.post('/tab-memory/bulk-delete', { ids, hard });
  }

  async emptyTabMemoryTrash(): Promise<void> {
    await this.client.delete('/tab-memory/trash');
  }

  async searchTabMemories(
    query: string,
    filters: { tag?: string; pinned?: boolean; groupId?: string } = {},
  ): Promise<any[]> {
    const params: Record<string, string> = { q: query };
    if (filters.tag) params.tag = filters.tag;
    if (filters.pinned) params.pinned = 'true';
    if (filters.groupId) params.group_id = filters.groupId;
    const response = await this.client.get('/tab-memory/search', { params });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
  }

  async exportTabMemories(format: 'json' | 'markdown' = 'json'): Promise<Blob> {
    const response = await this.client.get('/tab-memory/export', {
      params: { format },
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async importTabMemories(payload: { memories: any[] }): Promise<{ imported: number }> {
    const response = await this.client.post('/tab-memory/import', payload);
    return response.data ?? { imported: 0 };
  }

  // Collaboration API
  async getSessions(): Promise<any[]> {
    const response = await this.client.get('/collaboration/sessions');
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.sessions && Array.isArray(data.sessions)) return data.sessions;
    if (data?.users && Array.isArray(data.users)) return data.users;
    return [];
  }

  async getSession(id: string): Promise<any> {
    const response = await this.client.get(`/collaboration/sessions/${id}`);
    return response.data;
  }

  async createSession(data: any): Promise<any> {
    const response = await this.client.post('/collaboration/sessions', data);
    return response.data;
  }

  async updateSession(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/collaboration/sessions/${id}`, data);
    return response.data;
  }

  async deleteSession(id: string): Promise<void> {
    await this.client.delete(`/collaboration/sessions/${id}`);
  }

  async joinSession(inviteCode: string): Promise<any> {
    const response = await this.client.post('/collaboration/sessions/join', { inviteCode });
    return response.data;
  }

  async addAnnotation(sessionId: string, annotation: any): Promise<any> {
    const response = await this.client.post(`/collaboration/sessions/${sessionId}/annotations`, annotation);
    return response.data;
  }

  async addComment(sessionId: string, comment: any): Promise<any> {
    const response = await this.client.post(`/collaboration/sessions/${sessionId}/comments`, comment);
    return response.data;
  }

  async resolveAnnotation(sessionId: string, annotationId: string): Promise<void> {
    await this.client.patch(`/collaboration/sessions/${sessionId}/annotations/${annotationId}`);
  }

  // Mesh Network API
  async getMeshVisualization(): Promise<{ nodes: any[]; edges: any[] }> {
    const response = await this.client.get('/mesh-network/nodes');
    const data = response.data;
    if (Array.isArray(data)) return { nodes: data, edges: [] };
    return {
      nodes: Array.isArray(data?.nodes) ? data.nodes : [],
      edges: Array.isArray(data?.edges) ? data.edges : [],
    };
  }

  async getMeshNodes(): Promise<any[]> {
    const { nodes } = await this.getMeshVisualization();
    return nodes;
  }

  async createNode(data: any): Promise<any> {
    const response = await this.client.post('/mesh-network/nodes', data);
    return response.data;
  }

  async updateNode(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/mesh-network/nodes/${id}`, data);
    return response.data;
  }

  async deleteNode(id: string): Promise<void> {
    await this.client.delete(`/mesh-network/nodes/${id}`);
  }

  async getMeshStats(): Promise<{ nodes: any[]; edges: any[]; insights: any[] }> {
    const response = await this.client.get('/mesh-network/stats');
    const data = response.data ?? {};
    return {
      nodes: Array.isArray(data.nodes) ? data.nodes : [],
      edges: Array.isArray(data.edges) ? data.edges : [],
      insights: Array.isArray(data.insights) ? data.insights : [],
    };
  }

  async syncMeshFromTabs(): Promise<{ nodes: any[]; edges: any[]; insights: any[] }> {
    const response = await this.client.post('/mesh-network/relationships');
    const data = response.data ?? {};
    return {
      nodes: Array.isArray(data.nodes) ? data.nodes : [],
      edges: Array.isArray(data.edges) ? data.edges : [],
      insights: Array.isArray(data.insights) ? data.insights : [],
    };
  }

  async createMeshEdge(data: {
    source: string;
    target: string;
    strength?: number;
    type?: string;
    label?: string;
    bidirectional?: boolean;
  }): Promise<any> {
    const response = await this.client.post('/mesh-network/edges', data);
    return response.data;
  }

  async deleteMeshEdge(id: string): Promise<void> {
    await this.client.delete(`/mesh-network/edges/${id}`);
  }

  // Research synthesis (Soma-backed via Khiimori adapters; /api without /v1)
  async getTopics(): Promise<{ data: { topics: any[] } }> {
    const response = await this.client.get('/research-synthesis/topics');
    return response;
  }

  async createTopic(body: {
    name?: string;
    description?: string;
    session_id?: string;
    tab_ids?: string[];
    journal_format?: string;
    query?: string;
    expand_literature?: boolean;
  }): Promise<{ data: any }> {
    const response = await this.client.post('/research-synthesis/topics', body);
    return response;
  }

  async synthesizeTopic(
    topicId: string,
    body: {
      journal_format?: string;
      query?: string;
      title?: string;
      expand_literature?: boolean;
      tab_ids?: string[];
      session_id?: string;
    } = {},
  ): Promise<{ data: any }> {
    // Soma literature expand + LLM manuscript often exceeds the default 30s axios timeout.
    const response = await this.client.post(
      `/research-synthesis/topics/${topicId}/synthesize`,
      body,
      { timeout: 720_000 },
    );
    return response;
  }

  async createResearchPaper(body: {
    session_id?: string;
    tab_ids?: string[];
    title?: string;
    query?: string;
    journal_format?: string;
    expand_literature?: boolean;
  }): Promise<{
    id: string;
    app_id?: string;
    title?: string;
    status?: string;
    manuscript_markdown?: string;
    journal_format?: string;
    [key: string]: unknown;
  }> {
    const response = await this.mainClient.post('/research-papers', body, {
      timeout: 720_000,
    });
    return response.data;
  }

  async exportResearchPaper(
    paperId: string,
    format: string = 'markdown',
    template?: string,
  ): Promise<Blob> {
    const response = await this.mainClient.post(
      `/research-papers/${paperId}/export`,
      { format, template },
      { responseType: 'blob' },
    );
    return response.data as Blob;
  }

  // Data aggregation
  async getDataAggregationSources(): Promise<{ data: { sources: any[] } }> {
    const response = await this.client.get('/data-aggregation/sources');
    return response;
  }

  // Voice Command API
  async getVoiceCommands(): Promise<any[]> {
    const response = await this.client.get('/voice-command/commands');
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.commands && Array.isArray(data.commands)) return data.commands;
    return [];
  }

  async getVoiceCommandTemplates(): Promise<any[]> {
    const response = await this.client.get('/voice-command/templates');
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.templates && Array.isArray(data.templates)) return data.templates;
    return [];
  }

  async submitVoiceTranscript(body: { transcript: string; session_id?: string }): Promise<any> {
    const response = await this.client.post('/voice-command/commands', body);
    return response.data;
  }

  async confirmVoiceCommand(id: string): Promise<any> {
    const response = await this.client.put(`/voice-command/commands/${id}`, {});
    return response.data;
  }

  async createVoiceCommand(data: { transcript: string; session_id?: string }): Promise<any> {
    return this.submitVoiceTranscript(data);
  }

  async updateVoiceCommand(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/voice-command/commands/${id}`, data);
    return response.data;
  }

  async deleteVoiceCommand(id: string): Promise<void> {
    await this.client.delete(`/voice-command/commands/${id}`);
  }

  async getVoiceProfile(userId: string): Promise<any> {
    const response = await this.client.get(`/voice-command/profiles/${userId}`);
    return response.data?.profile ?? response.data;
  }

  async processVoiceCommand(audioData: FormData): Promise<any> {
    const response = await this.client.post('/voice-command/process', audioData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Version Control API
  async getVersions(tabId?: string): Promise<any[]> {
    // When `tabId` is omitted we fall back to the /version-control
    // list endpoint so the page can render a unified timeline
    // across every tab.
    const url = tabId
      ? `/version-control/tabs/${tabId}/versions`
      : `/version-control/versions`;
    const response = await this.client.get(url);
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.snapshots && Array.isArray(data.snapshots)) return data.snapshots;
    if (data?.versions && Array.isArray(data.versions)) return data.versions;
    return [];
  }

  async getTimeBookmarks(_tabId?: string): Promise<any[]> {
    const response = await this.client.get('/version-control/bookmarks');
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data?.bookmarks && Array.isArray(data.bookmarks)) return data.bookmarks;
    return [];
  }

  async createTimeBookmark(data: {
    tab_id?: string;
    name?: string;
    label?: string;
  }): Promise<any> {
    const response = await this.client.post('/version-control/bookmarks', data);
    return response.data?.bookmark ?? response.data;
  }

  async createVersion(tabId: string, data: any): Promise<any> {
    const response = await this.client.post(`/version-control/tabs/${tabId}/versions`, data);
    return response.data;
  }

  async restoreVersion(tabId: string, versionId: string): Promise<any> {
    const response = await this.client.post(`/version-control/tabs/${tabId}/versions/${versionId}/restore`);
    return response.data;
  }

  async compareVersions(tabId: string, v1: string, v2: string): Promise<any> {
    const response = await this.client.get(`/version-control/tabs/${tabId}/compare`, {
      params: { v1, v2 },
    });
    return response.data;
  }

  // AI Assistant API (Express /api/ai-assistant)
  async getAiConversationHistory(userId: string, limit = 20): Promise<any[]> {
    const response = await this.client.get(`/ai-assistant/conversations/history/${userId}`, {
      params: { limit },
    });
    return response.data;
  }

  async createAiConversation(data: {
    userId: string;
    sessionId: string;
    type?: 'general' | 'tab_context' | 'task_assist' | 'discovery' | 'troubleshooting';
    contextTabId?: string;
  }): Promise<any> {
    const response = await this.client.post('/ai-assistant/conversations', {
      userId: data.userId,
      sessionId: data.sessionId,
      type: data.type ?? 'general',
      contextTabId: data.contextTabId,
    });
    return response.data;
  }

  async addAiConversationMessage(
    conversationId: string,
    body: { role: 'user' | 'assistant' | 'system'; content: string; attachments?: any[]; contextData?: any }
  ): Promise<any> {
    const response = await this.client.post(`/ai-assistant/conversations/${conversationId}/messages`, body);
    return response.data;
  }

  async respondAiConversation(conversationId: string, message: string, mode?: string): Promise<{ response: string; messages?: unknown[] }> {
    const response = await this.client.post(`/ai-assistant/conversations/${conversationId}/respond`, {
      message,
      mode,
    }, { timeout: 120000 });
    return response.data;
  }

  async endAiConversation(conversationId: string): Promise<void> {
    await this.client.post(`/ai-assistant/conversations/${conversationId}/end`);
  }

  async chat(
    messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>,
    options?: { provider?: string; max_tokens?: number; temperature?: number }
  ): Promise<{ message: { role: string; content: string }; model: string; tokens_used: number; latency_ms: number }> {
    return requestLlmChatViaWebSocket(messages, options);
  }

  async getAiSuggestions(userId: string): Promise<any[]> {
    const response = await this.client.get(`/ai-assistant/suggestions/${userId}`);
    return response.data;
  }

  async recordAiSuggestionFeedback(suggestionId: string, feedback: 'accepted' | 'rejected'): Promise<void> {
    await this.client.post(`/ai-assistant/suggestions/${suggestionId}/feedback`, { feedback });
  }

  async getTabMemoryDashboard(userId: string): Promise<any> {
    const response = await this.client.get(`/tab-memory/dashboard/${userId}`);
    return response.data;
  }

  // Tab Preview API
  // Note: these endpoints are served by the main Go API at
  // /api/v1/tab-preview/... (see internal/api/routes.go). They are
  // *not* available on the advanced/Express API, so they must go
  // through `this.mainClient`.
  async getTabPreviews(sessionId?: string): Promise<TabPreview[]> {
    const response = await this.mainClient.get('/tab-preview/batch', {
      params: sessionId ? { session_id: sessionId } : undefined,
    });
    const data = response.data;
    if (Array.isArray(data?.previews)) return data.previews;
    if (Array.isArray(data)) return data;
    return [];
  }

  async getTabPreview(tabId: string): Promise<TabPreview | null> {
    try {
      const response = await this.mainClient.get(`/tab-preview/${tabId}`);
      return response.data ?? null;
    } catch {
      return null;
    }
  }

  async captureTabPreview(
    tabId: string,
    payload: { url: string; width?: number; height?: number; format?: string } = { url: '' },
  ): Promise<TabPreview> {
    const response = await this.mainClient.post(`/tab-preview/${tabId}/capture`, payload);
    return response.data;
  }

  async deleteTabPreview(tabId: string): Promise<void> {
    await this.mainClient.delete(`/tab-preview/${tabId}`);
  }

  async getTabPreviewStats(): Promise<TabPreviewStats> {
    const response = await this.mainClient.get('/tab-preview/stats');
    return response.data ?? {
      total_previews: 0,
      total_size_bytes: 0,
      stale_count: 0,
      by_format: {},
    };
  }

  // Smart Group API
  async getSmartGroups(): Promise<any> {
    const response = await this.client.get('/smart-group/groups');
    return response.data;
  }

  async createSmartGroup(data: any): Promise<any> {
    const response = await this.client.post('/smart-group/groups', data);
    return response.data;
  }

  async updateSmartGroup(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/smart-group/groups/${id}`, data);
    return response.data;
  }

  async deleteSmartGroup(id: string): Promise<void> {
    await this.client.delete(`/smart-group/groups/${id}`);
  }

  async evaluateGroupRules(): Promise<any> {
    const response = await this.client.post('/smart-group/groups/evaluate');
    return response.data;
  }

  async getGroupAnalytics(): Promise<any> {
    const response = await this.client.get('/smart-group/analytics');
    return response.data;
  }

  async getAutoRules(): Promise<any[]> {
    const response = await this.client.get('/smart-group/rules');
    return response.data;
  }

  async updateAutoRule(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/smart-group/rules/${id}`, data);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;

// Named export functions for convenience
export const getPatterns = async () => {
  return apiClient.getTabMemories();
};

export const createPrediction = async (data: any) => {
  return apiClient.createTabMemory(data);
};

export const deletePattern = async (id: string) => {
  return apiClient.deleteTabMemory(id);
};

export const getMeshGraph = async () => {
  const nodes = await apiClient.getMeshNodes();
  return { nodes };
};

export const createMeshNode = async (data: any) => {
  return apiClient.createNode(data);
};

export const deleteMeshNode = async (id: string) => {
  return apiClient.deleteNode(id);
};

export const getVoiceHistory = async () => {
  return apiClient.getVoiceCommands();
};

export const sendVoiceCommand = async (data: any) => {
  return apiClient.createVoiceCommand(data);
};

export const getCommandTemplates = async () => {
  return apiClient.getVoiceCommands();
};

export const deleteVoiceCommand = async (id: string) => {
  return apiClient.deleteVoiceCommand(id);
};

export const getSnapshots = async () => {
  return apiClient.getVersions('default');
};

export const restoreSnapshot = async (versionId: string) => {
  return apiClient.restoreVersion('default', versionId);
};

export const compareSnapshots = async (v1: string, v2: string) => {
  return apiClient.compareVersions('default', v1, v2);
};

/** Active AI conversations for user (each includes `messages` JSON). */
export const getChatHistory = async (userId: string, limit?: number) => {
  return apiClient.getAiConversationHistory(userId, limit);
};

export const sendMessage = async (conversationId: string, content: string, history: Array<{ role: 'user' | 'assistant'; content: string }> = []) => {
  const messages = [
    ...history.map(m => ({ role: m.role, content: m.content })),
    { role: 'user' as const, content }
  ];
  const response = await apiClient.chat(messages);
  return response;
};

export const chat = async (
  messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>,
  options?: { provider?: string; max_tokens?: number; temperature?: number }
) => {
  return apiClient.chat(messages, options);
};

export const getSuggestions = async (userId: string) => {
  return apiClient.getAiSuggestions(userId);
};

export const markSuggestionSeen = async (id: string) => {
  await apiClient.recordAiSuggestionFeedback(id, 'rejected');
};

export const getSmartGroups = async () => {
  return apiClient.getSmartGroups();
};

export const createSmartGroup = async (data: any) => {
  return apiClient.createSmartGroup(data);
};

export const deleteSmartGroup = async (id: string) => {
  return apiClient.deleteSmartGroup(id);
};

export const updateSmartGroup = async (id: string, data: any) => {
  return apiClient.updateSmartGroup(id, data);
};

export const getAutoRules = async () => {
  return apiClient.getAutoRules();
};

export const updateAutoRule = async (id: string, data: any) => {
  return apiClient.updateAutoRule(id, data);
};

export const getCollaborators = async () => {
  const sessions = await apiClient.getSessions();
  return sessions;
};

export const createSession = async (data: any) => {
  return apiClient.createSession(data);
};

export const joinSession = async (inviteCode: string) => {
  return apiClient.joinSession(inviteCode);
};

export const addAnnotation = async (sessionId: string, annotation: any) => {
  return apiClient.addAnnotation(sessionId, annotation);
};

export const addComment = async (sessionId: string, comment: any) => {
  return apiClient.addComment(sessionId, comment);
};

export const getMeshStats = async () => {
  return apiClient.getMeshStats();
};

export const searchTabMemories = async (query: string) => {
  return apiClient.searchTabMemories(query);
};

export const getTabPreviews = async (sessionId?: string): Promise<TabPreview[]> => {
  return apiClient.getTabPreviews(sessionId);
};

export const captureTabPreview = async (
  tabId: string,
  payload: { url: string; width?: number; height?: number; format?: string },
): Promise<TabPreview> => {
  return apiClient.captureTabPreview(tabId, payload);
};

export const deleteTabPreview = async (tabId: string): Promise<void> => {
  return apiClient.deleteTabPreview(tabId);
};

export const getTabPreviewStats = async (): Promise<TabPreviewStats> => {
  return apiClient.getTabPreviewStats();
};